/**
 * DENSITY AND COLUMN VISIBILITY, IN THIS BROWSER.
 *
 * The only thing the Dashboard persists outside the URL. It is per DEVICE and
 * deliberately so: how much fits on the screen in front of you is genuinely a
 * different answer on a 13" laptop and a 27" desk monitor, so syncing it would
 * mean the desk machine's twelve columns arriving on the laptop — the opposite
 * of the preference being honoured. There is no table for it and there should
 * not be one.
 *
 * Everything else the screen knows lives in the query string, which is why a
 * link is the whole state and there is nothing else to store.
 */
import { DashboardFilter } from '../state/filter-model';

const PREFS_KEY = 'lobbero.dashboard.prefs';

export interface DevicePrefs {
  density?: DashboardFilter['density'];
  columns?: Record<string, string[]>;
  /** Whether the advanced filter rail is expanded at lg+ (QA-07). Ergonomics,
   *  like density: the right answer differs between a 13-inch laptop and a
   *  27-inch monitor, and it says nothing about the data. */
  railOpen?: boolean;
}

export const readPrefs = (): DevicePrefs => {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);

    return raw ? JSON.parse(raw) : {};
  } catch {
    // A corrupt blob must not take the screen down. An unreadable store is an
    // empty one.
    return {};
  }
};

export const writePrefs = (prefs: DevicePrefs): void => {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* A full or blocked storage is not worth an error dialog. */
  }
};

/* ── THE ADVANCED RAIL'S DISCLOSURE STATE (QA-07) ──────────────────────────
 *
 * Its own pair rather than a `readPrefs().railOpen` at each call site, because
 * the DEFAULT is the load-bearing part: `false` when nothing is stored, which is
 * the whole point of the change. Spelling that default at two call sites is how
 * one of them ends up defaulting to open.
 *
 * The read is guarded the same way readPrefs is: a private window, a blocked
 * store or a thumbnail capture can THROW on access rather than return null, and
 * the screen must render either way.
 */
export const readRailOpen = (): boolean => {
  try {
    return readPrefs().railOpen === true;
  } catch {
    return false;
  }
};

export const writeRailOpen = (open: boolean): void => {
  try {
    writePrefs({ ...readPrefs(), railOpen: open });
  } catch {
    /* Not worth an error dialog; the rail simply forgets. */
  }
};
